import React, {useState, useEffect, useMemo} from 'react';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Card,
    CardContent,
    Chip
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    People as PeopleIcon,
    DateRange as DateRangeIcon,
    CalendarMonth as CalendarMonthIcon
} from '@mui/icons-material';
import {LineChart} from '@mui/x-charts/LineChart';
import {createApiClient} from '../services/ApiClient';

const UserAnalyticsChart = ({accessToken}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    // Create API client
    const apiClient = useMemo(() => {
        return accessToken ? createApiClient(accessToken) : null;
    }, [accessToken]);

    // Fetch analytics data
    const fetchAnalyticsData = async () => {
        if (!apiClient) return;

        try {
            setLoading(true);
            setError(null);

            const response = await apiClient.get('/api/admin/analytics/dashboard', {
                date: selectedDate
            });

            setData(response);
        } catch (error) {
            console.error('Failed to fetch analytics data:', error);
            setError(error.message || 'Failed to load analytics data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (apiClient) {
            fetchAnalyticsData();
        }
    }, [apiClient, selectedDate]);

    // Prepare chart data
    const chartData = useMemo(() => {
        if (!data?.activeUserData?.dauCurve || !Array.isArray(data.activeUserData.dauCurve)) return null;

        const dauCurve = data.activeUserData.dauCurve;

        // Sort by date and extract data
        const sortedData = dauCurve.sort((a, b) => new Date(a.date) - new Date(b.date));
        const dates = sortedData.map(item => new Date(item.date));
        const values = sortedData.map(item => item.count || 0);

        return {
            xAxisData: dates,
            series: [
                {
                    data: values,
                    label: 'Daily Active Users',
                    curve: 'catmullRom',
                    color: '#1976d2'
                }
            ]
        };
    }, [data]);

    // Format number with commas
    const formatNumber = (num) => {
        if (num === null || num === undefined) return 'N/A';
        return new Intl.NumberFormat().format(num);
    };

    // Get trend indicator
    const getTrendIndicator = (current, previous) => {
        if (!current || !previous) return null;
        const change = current - previous;
        const isPositive = change >= 0;
        return {
            value: Math.abs(change),
            isPositive,
            percentage: previous > 0 ? Math.abs((change / previous) * 100).toFixed(1) : 0
        };
    };

    if (loading) {
        return (
            <Paper sx={{p: 3, textAlign: 'center'}}>
                <CircularProgress sx={{mb: 2}}/>
                <Typography variant="body2" color="text.secondary">
                    Loading user analytics...
                </Typography>
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper sx={{p: 3}}>
                <Alert severity="error">
                    {error}
                </Alert>
            </Paper>
        );
    }

    if (!data) {
        return (
            <Paper sx={{p: 3}}>
                <Alert severity="info">
                    No analytics data available
                </Alert>
            </Paper>
        );
    }

    const {newUserCount, activeUserData} = data;
    const dauTrend = getTrendIndicator(activeUserData?.dau?.count, activeUserData?.dau?.delta);
    const wauTrend = getTrendIndicator(activeUserData?.wau?.count, activeUserData?.wau?.delta);
    const mauTrend = getTrendIndicator(activeUserData?.mau?.count, activeUserData?.mau?.delta);
    const weeklyTrend = getTrendIndicator(newUserCount?.last7Days?.count, newUserCount?.last7Days?.delta);

    return (
        <Box>
            {/* Header with date selector */}
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3}}>
                <Typography variant="h5" component="h2">
                    User Trends
                </Typography>
                <FormControl size="small" sx={{minWidth: 200}}>
                    <InputLabel>Date</InputLabel>
                    <Select
                        value={selectedDate}
                        label="Date"
                        onChange={(e) => setSelectedDate(e.target.value)}
                    >
                        {/* Generate last 30 days */}
                        {Array.from({length: 30}, (_, i) => {
                            const date = new Date();
                            date.setDate(date.getDate() - i);
                            const dateStr = date.toISOString().split('T')[0];
                            const displayDate = date.toLocaleDateString();
                            return (
                                <MenuItem key={dateStr} value={dateStr}>
                                    {displayDate}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </Box>

            {/* Metrics Cards */}
            <Grid container spacing={3} sx={{mb: 4}}>
                {/* Daily Active Users */}
                <Grid size={{xs: 12, sm: 6, lg: 3}}>
                    <Card sx={{height: '100%'}}>
                        <CardContent>
                            <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                                <DateRangeIcon color="info" sx={{mr: 1}}/>
                                <Typography variant="h6" component="div">
                                    DAU
                                </Typography>
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                <Typography variant="h4" color="info.main">
                                    {formatNumber(activeUserData?.dau?.count)}
                                </Typography>
                                {dauTrend && (
                                    <Chip
                                        label={`${dauTrend.isPositive ? '+' : '-'}${dauTrend.value}`}
                                        color={dauTrend.isPositive ? 'success' : 'error'}
                                        size="small"
                                        icon={<TrendingUpIcon/>}
                                    />
                                )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                Daily Active Users
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Weekly Active Users */}
                <Grid size={{xs: 12, sm: 6, lg: 3}}>
                    <Card sx={{height: '100%'}}>
                        <CardContent>
                            <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                                <DateRangeIcon color="secondary" sx={{mr: 1}}/>
                                <Typography variant="h6" component="div">
                                    WAU
                                </Typography>
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                <Typography variant="h4" color="secondary.main">
                                    {formatNumber(activeUserData?.wau?.count)}
                                </Typography>
                                {wauTrend && (
                                    <Chip
                                        label={`${wauTrend.isPositive ? '+' : '-'}${wauTrend.value}`}
                                        color={wauTrend.isPositive ? 'success' : 'error'}
                                        size="small"
                                        icon={<TrendingUpIcon/>}
                                    />
                                )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                Weekly Active Users
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Monthly Active Users */}
                <Grid size={{xs: 12, sm: 6, lg: 3}}>
                    <Card sx={{height: '100%'}}>
                        <CardContent>
                            <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                                <CalendarMonthIcon color="primary" sx={{mr: 1}}/>
                                <Typography variant="h6" component="div">
                                    MAU
                                </Typography>
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                <Typography variant="h4" color="primary.main">
                                    {formatNumber(activeUserData?.mau?.count)}
                                </Typography>
                                {mauTrend && (
                                    <Chip
                                        label={`${mauTrend.isPositive ? '+' : '-'}${mauTrend.value}`}
                                        color={mauTrend.isPositive ? 'success' : 'error'}
                                        size="small"
                                        icon={<TrendingUpIcon/>}
                                    />
                                )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                Monthly Active Users
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* New Users Last 7 Days */}
                <Grid size={{xs: 12, sm: 6, lg: 3}}>
                    <Card sx={{height: '100%'}}>
                        <CardContent>
                            <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                                <PeopleIcon color="success" sx={{mr: 1}}/>
                                <Typography variant="h6" component="div">
                                    New (7 Days)
                                </Typography>
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                <Typography variant="h4" color="success.main">
                                    {formatNumber(newUserCount?.last7Days?.count)}
                                </Typography>
                                {weeklyTrend && (
                                    <Chip
                                        label={`${weeklyTrend.isPositive ? '+' : '-'}${weeklyTrend.value}`}
                                        color={weeklyTrend.isPositive ? 'success' : 'error'}
                                        size="small"
                                        icon={<TrendingUpIcon/>}
                                    />
                                )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                Average: {formatNumber(Math.round((newUserCount?.last7Days?.count || 0) / 7))} per day
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Line Chart */}
            {chartData && (
                <Paper sx={{p: 3}}>
                    <Typography variant="h6" gutterBottom>
                        Daily Active Users Trend
                    </Typography>
                    <Box sx={{width: '100%', height: 380}}>
                        <LineChart
                            height={380}
                            series={chartData.series}
                            xAxis={[{
                                data: chartData.xAxisData,
                                scaleType: 'time',
                                tickNumber: 5,
                                valueFormatter: (date) => {
                                    const options = {month: 'short', day: 'numeric'};
                                    return date.toLocaleDateString('en-US', options);
                                }
                            }]}
                            yAxis={[{
                                valueFormatter: (value) => Math.round(value).toString()
                            }]}
                            grid={{vertical: true, horizontal: true}}
                            slotProps={{
                                legend: {hidden: true}
                            }}
                            sx={{
                                '& .MuiLineElement-root': {
                                    strokeWidth: 2,
                                },
                                '& .MuiMarkElement-root': {
                                    strokeWidth: 1,
                                    r: 3,
                                },
                                '& .MuiChartsGrid-line': {
                                    strokeDasharray: '3 3',
                                    strokeOpacity: 0.7,
                                },
                            }}
                        />
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default UserAnalyticsChart;